import { useState } from "react";
import { useNavigate } from "react-router-dom";
import logo from "@/assets/logo.png";
import { Home, Activity, Users, MessageSquareQuote, LogOut } from "lucide-react";

const API_URL = import.meta.env.VITE_API_URL;

function Sidebar({ currentPage, onNavigate, open, onClose }) {
  const isOpen = open ?? false;
  const navigate = useNavigate();
  const [showLogout, setShowLogout] = useState(false);

  const navItems = [
    { id: "dashboard", label: "Dashboard", icon: Home },
    { id: "sda_log", label: "SDA Log", icon: Activity },
    { id: "admins", label: "Users", icon: Users },
    { id: "testimonials", label: "Testimonials", icon: MessageSquareQuote },
  ];

  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/api/admin/logout`, {
        method: "POST",
        credentials: "include", // required to clear Flask session
      });
    } catch (error) {
      console.error("Logout failed:", error);
    }

    // Clear local data
    localStorage.removeItem("role");
    localStorage.removeItem("username");
    localStorage.removeItem("accessToken");
    sessionStorage.removeItem("accessToken");

    // Redirect to login
    navigate("/login");
  };

  return (
    <>
      {isOpen && (
        <button
          type="button"
          onClick={() => onClose?.()}
          className="fixed inset-0 z-40 bg-black/30 md:hidden"
          aria-label="Close sidebar"
        />
      )}

      <aside
        className={`fixed left-0 top-0 z-50 h-full w-72 flex flex-col bg-white border-r border-gray-200 transform transition-transform duration-200 will-change-transform
  ${
    isOpen
      ? "translate-x-0 pointer-events-auto"
      : "-translate-x-full pointer-events-none"
  }
  md:translate-x-0 md:transform-none md:pointer-events-auto`}>
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-6 border-b border-gray-200">
          <img src={logo} alt="Logo" className="h-16 w-full object-contain" />
        </div>

        {/* Navigation links */}
        <nav className="flex-1 p-4 space-y-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = item.id === currentPage;
            return (
              <button
                key={item.id}
                onClick={() => {
                  onNavigate(item.id);
                  onClose && onClose();
                }}
                className={`w-full flex items-center gap-3 rounded-lg px-4 py-3 transition-colors ${
                  isActive
                    ? "bg-[#D2138C] text-white"
                    : "text-gray-700 hover:bg-gray-100"
                }`}>
                <Icon className="h-5 w-5" />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Logout */}
        <div className="border-t border-gray-200 px-4 py-3">
          <button
            onClick={() => setShowLogout(true)}
            className="w-full flex items-center gap-3 rounded-lg px-4 py-3 text-gray-700 hover:bg-rose-50 hover:text-rose-600 transition-colors">
            <LogOut className="h-5 w-5" />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Styled logout modal */}
      {showLogout && (
        <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/30 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm mx-4">
            <div className="px-6 pt-6">
              <h3 className="text-lg font-semibold text-gray-900">Sign out?</h3>
              <p className="mt-2 text-sm text-gray-500">
                Are you sure you want to sign out? You will need to login again
                to access the admin dashboard.
              </p>
            </div>
            <div className="flex justify-end gap-3 px-6 py-5 bg-gray-50 rounded-b-xl">
              <button
                onClick={() => setShowLogout(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-100">
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowLogout(false);
                  handleLogout();
                }}
                className="px-4 py-2 text-sm font-semibold text-white bg-[#D2138C] rounded-lg hover:bg-[#b01074]">
                Sign out
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Sidebar;
